import React, { useEffect, useRef, useState } from 'react'
import { Container, Box, Grid, Paper, Button, Typography, makeStyles } from '@material-ui/core'
import AddIcon from '@material-ui/icons/AddRounded'
import 'moment/locale/th'
import { useSettingHoliday, HolidayDetail } from '../services/um/useSettingHoliday'
import useLoading from '../control/loading/useLoading'
import HolidayCreateDialog from '../component/um/SettingHoliday/HolidayCreateDialog'
import HolidayEditDialog from '../component/um/SettingHoliday/HolidayEditDialog'
import Dialog, { DialogType } from '../control/dialog/dialog'
import useTranslate from '../i18n/useTranslate'

type DialogRef = { openDialog?: () => void; closeDialog?: () => void }

const SettingHoliday: React.FC = () => {
    const translate = useTranslate()
    const classes = useStyles()
    const { getHolidaySetting, deleteHolidaySetting } = useSettingHoliday()
    const loadingHook = useLoading()
    const lang = translate.languageStore.currentLanguage

    const [openCreate, setOpenCreate] = useState(false)
    const [editing, setEditing] = useState<HolidayDetail>()
    const [holidayList, setHolidayList] = useState<HolidayDetail[]>([])

    const deleteDialogRef = useRef<DialogRef | null>(null)
    const successDialogRef = useRef<DialogRef | null>(null)

    const [selectedDeleteId, setSelectedDeleteId] = useState<number | null>(null)

    const [successConfig, setSuccessConfig] =
        useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm'>>()

    const fetchHolidayData = async () => {
        loadingHook.loadingOpen()
        try {
            const res = await getHolidaySetting()
            if (res) setHolidayList(res)
        } finally {
            loadingHook.loadingClose()
        }
    }

    useEffect(() => {
        fetchHolidayData()
    }, [])

    const openConfirmDelete = (holidayId: number) => {
        setSelectedDeleteId(holidayId)
        deleteDialogRef.current?.openDialog?.()
    }

    const bindDeleteRef = (func: Record<string, Function>) => {
        deleteDialogRef.current = func as unknown as DialogRef
    }

    const bindSuccessRef = (func: Record<string, Function>) => {
        successDialogRef.current = func as unknown as DialogRef
    }

    const notifySuccess = (type: 'create' | 'edit' | 'delete') => {
        const title = translate.get('ALERT.TITLE.NOTIFICATION')
        const contentMap = {
            create: translate.get('ALERT.CONTENT.SAVE_SUCCESS'),
            edit: translate.get('ALERT.CONTENT.SAVE_SUCCESS'),
            delete: translate.get('ALERT.CONTENT.DELETE_SUCCESS'),
        }

        setSuccessConfig({
            title,
            content: contentMap[type],
            type: 'alert-message',
            onClickConfirm: () => successDialogRef.current?.closeDialog?.(),
        })
        successDialogRef.current?.openDialog?.()
    }

    const handleDelete = async () => {
        if (!selectedDeleteId) return
        loadingHook.loadingOpen()
        try {
            await deleteHolidaySetting(selectedDeleteId)
            await fetchHolidayData()
            setSelectedDeleteId(null)
            deleteDialogRef.current?.closeDialog?.()
            notifySuccess('delete')
        } finally {
            loadingHook.loadingClose()
        }
    }

    const formatDate = (month: number, day: number) => {
        const d = new Date(2000, month - 1, day)
        if (isNaN(d.getTime())) return '-'

        const locale = lang === 'th' ? 'th-TH' : 'en-GB'
        const parts = new Intl.DateTimeFormat(locale, {
            day: 'numeric',
            month: 'long',
        }).formatToParts(d)

        const dayStr = parts.find((p) => p.type === 'day')?.value
        const monthStr = parts.find((p) => p.type === 'month')?.value

        return `${dayStr} ${monthStr}`
    }

    return (
        <Container maxWidth="lg">
            <Box className={classes.headerRow}>
                <Typography variant="h5" className={classes.title}>
                    {translate.get('UM.SETTING_HOLIDAY.HEADER')}
                </Typography>
                <Button
                    startIcon={<AddIcon />}
                    className={classes.addBtn}
                    variant="contained"
                    onClick={() => setOpenCreate(true)}
                >
                    {translate.get('UM.SETTING_HOLIDAY.ADD_HOLIDAY')}
                </Button>
            </Box>

            {holidayList.length === 0 && (
                <Typography variant="body2" align="center" color="textSecondary" style={{ marginTop: 16 }}>
                    {translate.get('LABEL.NO_DATA_FOUND')}
                </Typography>
            )}

            {holidayList.map((h) => (
                <Paper key={h.holidayID} elevation={0} className={classes.item}>
                    <div className={classes.actions}>
                        <Button size="small" variant="contained" onClick={() => setEditing(h)}>
                            {translate.get('BUTTON.EDIT')}
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() => openConfirmDelete(h.holidayID)}
                        >
                            {translate.get('BUTTON.DELETE')}
                        </Button>
                    </div>

                    <Grid container alignItems="center" spacing={4}>
                        <Grid item className={classes.col}>
                            <Typography variant="caption" className={classes.label}>
                                {translate.get('UM.SETTING_HOLIDAY.DATE')}
                            </Typography>
                            <Typography variant="body1" className={classes.dateLink}>
                                {formatDate(h.month, h.day)}
                            </Typography>
                        </Grid>
                        <Grid item xs>
                            <Typography variant="caption" className={classes.label}>
                                {translate.get('UM.SETTING_HOLIDAY.HOLIDAY_NAME')}
                            </Typography>
                            <Typography variant="body1" style={{ fontWeight: 700 }}>
                                {lang === 'th' ? (h.holidayName?.th ?? '-') : (h.holidayName?.en ?? '-')}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            ))}

            <HolidayCreateDialog
                open={openCreate}
                onCancel={() => setOpenCreate(false)}
                onSuccess={() => {
                    setOpenCreate(false)
                    fetchHolidayData()
                    notifySuccess('create')
                }}
            />

            <HolidayEditDialog
                open={!!editing}
                initialValue={{ ...editing, nameTH: editing?.holidayName?.th, nameEN: editing?.holidayName?.en }}
                onCancel={() => setEditing(undefined)}
                onSuccess={() => {
                    setEditing(undefined)
                    fetchHolidayData()
                    notifySuccess('edit')
                }}
            />

            <Dialog
                onRef={bindDeleteRef}
                type="alert-confirm"
                title={translate.get('ALERT.TITLE.WARNING')}
                content={translate.get('ALERT.CONTENT.NEED_TO_DELETE')}
                onClickConfirm={handleDelete}
            />

            <Dialog onRef={bindSuccessRef} {...successConfig} />
        </Container>
    )
}

const useStyles = makeStyles((theme) => ({
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(3),
    },
    title: { fontWeight: 700 },
    addBtn: { background: '#DB5F8E', color: '#fff', '&:hover': { background: '#c54d7f' }, textTransform: 'none' },
    item: {
        padding: theme.spacing(2),
        borderRadius: 8,
        border: '1px solid #E9E9EF',
        marginBottom: theme.spacing(1.5),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    actions: { display: 'flex', gap: theme.spacing(1), marginRight: theme.spacing(2) },
    col: { minWidth: 220 },
    label: { color: theme.palette.text.secondary },
    dateLink: { color: theme.palette.info.main, cursor: 'pointer', fontWeight: 700 },
}))

export default SettingHoliday
